import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

type UseHasMaxHeightParams = {
	sidebarEl: React.MutableRefObject<HTMLElement | null>;
	isRefSet: boolean;
	isSticky: boolean;
	isFabricPage?: boolean;
};

export const useHasMaxHeight = ({
	sidebarEl,
	isRefSet,
	isSticky,
	isFabricPage,
}: UseHasMaxHeightParams): boolean => {
	const [hasMaxHeight, setHasMaxHeight] = useState(false);

	// Shared max height check logic
	const checkMaxHeight = useCallback(() => {
		if (!sidebarEl.current) return;

		if (isSticky) {
			setHasMaxHeight(true);
			return;
		}

		const rect = sidebarEl.current.getBoundingClientRect();
		const availableHeight = window.innerHeight - 300;

		setHasMaxHeight(rect.height > availableHeight);
	}, [isSticky, sidebarEl]);

	// Max height effect for pre-render (useLayoutEffect)
	useLayoutEffect(() => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		if (!sidebarEl.current) return;

		if (!expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		checkMaxHeight();
	}, [checkMaxHeight, isRefSet, isFabricPage, sidebarEl]);

	// Max height effect for post-render (useEffect)
	useEffect(() => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		if (!sidebarEl.current) return;

		if (expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		checkMaxHeight();
	}, [checkMaxHeight, isRefSet, isFabricPage, sidebarEl]);

	return hasMaxHeight;
};
